import React, { useEffect } from 'react';
import { useSearchParams, useNavigate, useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { Alert, Button, Container, Grid, Paper, Toolbar, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import i18next from 'i18next';
import { extractClientErrors, extractClientErrorsSource } from 'helper/apollo';
import _ from 'lodash';
import * as Sentry from '@sentry/react';

/**
 * Component that redirects to error page with error details.
 * Extracts error source information and navigates to error route.
 */
export function RedirectError(props: { err: any }) {
  const navigate = useNavigate();
  useEffect(() => {
    const currentUrl = window.location.pathname + window.location.search;
    if (props.err) {
      navigate(`/error?err=${JSON.stringify(extractClientErrorsSource(props.err))}&source=${encodeURIComponent(currentUrl)}`);
    } else {
      navigate(`/error?source=${encodeURIComponent(currentUrl)}`);
    }
  }, []);
  return <></>;
}

/**
 * Alert component that displays error messages extracted from GraphQL errors.
 * Renders multiple error messages as separate alerts.
 */
export function ErrorAlert(props: { err: any }) {
  const errs = props.err ? extractClientErrors(props.err) : null;
  if (errs) {
    return (
      <>
        {errs.map((e, i) => (
          <Alert key={i} severity="error">
            {e}
          </Alert>
        ))}
      </>
    );
  }
  return <></>;
}

/**
 * Error page component that displays error details and sends errors to Sentry.
 * Handles both route errors and query parameter errors with deduplication.
 * Shows error details dialog and Sentry event ID for tracking.
 */
export default function ErrorPage() {
  const [searchParams] = useSearchParams();
  const routeError = useRouteError();
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [sentryEventId, setSentryEventId] = React.useState<string | null>(null);

  const errMsgs: string[] = [];

  const errParam = searchParams.get('err');
  const sourceUrl = searchParams.get('source') || document.referrer || window.location.href;
  
  if (errParam) {
    try {
      const errs = JSON.parse(errParam);
      if (_.isArray(errs)) errMsgs.push(...errs);
      else errMsgs.push(`${errs}`);
    } catch (e) {
      errMsgs.push(`${errParam}`);
    }
  }

  if (!errParam && routeError) {
    if (isRouteErrorResponse(routeError)) {
      errMsgs.push(`RouteErrorResponse: ${routeError.status} ${routeError.statusText}`);
      if (routeError.data) {
        if (_.isString(routeError.data)) {
          errMsgs.push(routeError.data);
        } else {
          try {
            errMsgs.push(JSON.stringify(routeError.data, null, 2));
          } catch {
            errMsgs.push(`${routeError.data}`);
          }
        }
      }
    } else if (routeError instanceof Error) {
      errMsgs.push(routeError.message || 'Unknown route error');
      if (routeError.stack) errMsgs.push(routeError.stack);
    } else if (_.isString(routeError)) {
      errMsgs.push(routeError);
    } else {
      try {
        errMsgs.push(JSON.stringify(routeError, null, 2));
      } catch {
        errMsgs.push(`${routeError}`);
      }
    }
  }

  // Send error to Sentry once (with sessionStorage dedupe)
  useEffect(() => {
    if (!errParam && !routeError) return;

    // Safe hash function (btoa can fail with unicode)
    const getErrorHash = (key: string): string => {
      try {
        return btoa(encodeURIComponent(key)).substring(0, 50);
      } catch {
        // Fallback for unicode errors
        return key.substring(0, 50).replace(/[^a-zA-Z0-9]/g, '-');
      }
    };

    // Check if already sent in this session
    const rawErrorKey = errParam || errMsgs.join('; ') || 'unknown-route-error';
    const storageKey = `sentry-error-${getErrorHash(rawErrorKey)}`;
    const existingEventId = sessionStorage.getItem(storageKey);
    
    if (existingEventId && existingEventId !== 'sent') {
      // Use existing event ID (ignore old 'sent' values)
      setSentryEventId(existingEventId);
      return;
    }

    // Capture as Exception (not Message) for better stack traces
    const error = new Error(errMsgs.join('; ') || 'Unknown error');
    error.name = 'ErrorPageError';

    // Set transaction name to original page for better Sentry grouping
    Sentry.setContext('page', {
      originalUrl: sourceUrl,
      errorPageUrl: window.location.href,
    });

    const eventId = Sentry.captureException(error, {
      tags: {
        errorSource: 'ErrorPage',
        errorType: 'redirectError',
        originalPage: sourceUrl,
      },
      contexts: {
        errorDetails: {
          rawError: rawErrorKey,
          parsedErrors: errMsgs,
          sourceUrl: sourceUrl,
        },
      },
    });

    if (eventId) {
      setSentryEventId(eventId);
      sessionStorage.setItem(storageKey, eventId);
    }
  }, [errParam, routeError, errMsgs.join(',')]); // Join for stable comparison

  return (
    <>
      <Toolbar />
      <Container component="main" maxWidth="lg">
        <Paper>
          <Grid container direction="column" padding={3} spacing={3} justifyContent="center" alignItems="center">
            <Grid item sx={{ width: '100%' }}>
              <Alert severity="error">
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {i18next.t('error-generic')}
                </Typography>
                {process.env.REACT_APP_SENTRY_DSN && (
                  <Typography variant="body1" display="block" sx={{ mb: 2 }}>
                    {i18next.t('error-team-notified')}
                  </Typography>
                )}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item>
                    <Button variant="text" size="small" onClick={() => window.history.back()}>
                      {i18next.t('error-refresh')}
                    </Button>
                  </Grid>
                  {errMsgs.length > 0 && (
                    <Grid item>
                      <Button variant="text" size="small" onClick={() => setDetailsOpen(true)}>
                        {i18next.t('error-details-button')}
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Alert>
            </Grid>
          </Grid>
        </Paper>

        <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>{i18next.t('error-details-title')}</DialogTitle>
          <DialogContent>
            {sentryEventId && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  {i18next.t('error-event-id')} <strong>{sentryEventId}</strong>
                </Typography>
              </Alert>
            )}
            {errMsgs.map((e, i) => (
              <Typography
                key={i}
                variant="body2"
                component="pre"
                sx={{
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  mb: errMsgs.length > 1 ? 2 : 0,
                }}
              >
                {e}
              </Typography>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsOpen(false)} color="primary">
              {i18next.t('error-details-close')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}
